import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import Loading from "../../components/skeleton/loading";
import CommonForm from "../../components/form/form";
import {
  createJobApplication,
  getJobopeningDetailOpen,
} from "../../../services/workspace/recruitment.service";

const JobApplicationForm = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!id) return;
    getJobopeningDetailOpen(id)
      .then((res) => {
        setData(res?.jobOpening || null);
      })
      .catch(() => {
        toast.error(
          "Something went wrong. Please try again and verify the link."
        );
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading || !data) {
    return (
      <div className="min-h-screen">
        <Loading />
      </div>
    );
  }

  const DetailCard = ({ label, value }) => (
    <div className="grid grid-cols-2">
      <span className="text-sm text-theme/60">{label}</span>
      <span className="text-sm font-semibold">{value || "-"}</span>
    </div>
  );

  const formFields = [
    {
      name: "full_name",
      label: "Full Name",
      type: "text",
      required: true,
      colSpan: 6,
    },
    { name: "email", label: "Email", type: "text", required: true, colSpan: 6 },
    {
      name: "phone",
      label: "Phone Number",
      type: "text",
      required: true,
      colSpan: 6,
    },
    {
      name: "education",
      label: "Education",
      type: "text",
      required: false,
      colSpan: 6,
    },
    {
      name: "experience",
      label: "Experience",
      type: "select",
      required: true,
      options: [
        { label: "Fresher", value: "0" },
        { label: "1 Year", value: "1" },
        { label: "2 Years", value: "2" },
        { label: "3 Years", value: "3" },
        { label: "4+ Years", value: "4" },
      ],
      colSpan: 6,
    },

    { name: "current_role", label: "Current Role", type: "text", colSpan: 6 },
    {
      name: "current_company_name",
      label: "Current Company Name",
      type: "text",
      colSpan: 6,
    },
    {
      name: "reason_of_leaving",
      label: "Reason Of Leaving",
      type: "text",
      colSpan: 6,
    },
    { name: "current_ctc", label: "Current CTC", type: "text", colSpan: 6 },
    { name: "expected_ctc", label: "Expected CTC", type: "text", colSpan: 6 },
    { name: "key_skills", label: "Key Skills", type: "text", colSpan: 6 },
    {
      name: "communication_skills",
      label: "Communication Skills",
      type: "text",
      colSpan: 6,
    },

    {
      name: "availability_to_join",
      label: "Availability to Join",
      type: "select",
      options: [
        { label: "Immediate", value: "immediate" },
        { label: "15 Days", value: "15" },
        { label: "30 Days", value: "30" },
        { label: "45 Days", value: "45" },
      ],
      colSpan: 6,
    },

    {
      name: "notice_period",
      label: "Notice Period",
      type: "text",
      colSpan: 6,
    },

    { name: "address", label: "Address", type: "text", colSpan: 6 },

    {
      name: "where_are_you_from",
      label: "Where do you come from",
      type: "select",
      options: [
        { label: "Local", value: "local" },
        { label: "Other City", value: "other_city" },
        { label: "Other State", value: "other_state" },
      ],
      colSpan: 6,
    },

    { name: "resume", label: "Upload Resume", type: "file", colSpan: 12 },
  ];

  const basicDetails = [
    { label: "Department", value: data?.department?.name },
    { label: "Employment Type", value: data?.employment_type },
    { label: "Work Mode", value: data?.work_mode },
    { label: "No. Of Vacancies", value: data?.no_of_vacancies },
    { label: "Minimum Education", value: data?.minimum_education },
    { label: "Preferred Education", value: data?.preferred_education },
    {
      label: "Experience Required",
      value: `${data?.minimum_experience || 0} - ${
        data?.maximum_experience || 0
      } years`,
    },
    {
      label: "Salary Range",
      value: `${data?.salary_min || 0} - ${data?.salary_max || 0} / ${
        data?.salary_type || ""
      }`,
    },
    { label: "Posted On", value: data?.created_at },
    { label: "Closing On", value: data?.closing_date },
    { label: "Address", value: data?.address },
    { label: "Status", value: data?.status },
  ];

  const extraSections = [
    { title: "Key Skills", value: data?.key_skills },
    { title: "Job Responsibilities", value: data?.job_responsibilities },
    { title: "Perks & Benefits", value: data?.perks_benefits },
  ];

  const handleSubmit = async (formData) => {
    try {
      // Extract file URLs from successfully uploaded resume files
      const documentUrls = Array.isArray(formData.resume)
        ? formData.resume.name // Remove empty strings
        : [];

      const payload = {
        job_opening_id: data?.id,
        name: formData.full_name,
        email: formData.email,
        phone_number: formData.phone,
        education: formData.education,
        address: formData.address,
        experience: formData.experience, // already select value
        current_role: formData.current_role,
        current_company: formData.current_company_name,
        reason_of_leaving: formData.reason_of_leaving,
        current_ctc: formData.current_ctc,
        expected_ctc: formData.expected_ctc,

        key_skills:
          typeof formData.key_skills === "string"
            ? formData.key_skills.split(",").map((x) => x.trim())
            : [],

        communication_skills: formData.communication_skills,

        availability_to_join: formData.availability_to_join,
        notice_period: formData.notice_period,
        hear_from: formData.where_are_you_from,

        documents: documentUrls,
      };

      const res = await createJobApplication(payload);

      toast.success("Application submitted successfully!");
      navigate("/thank-you");
    } catch (error) {
      toast.error("Failed to submit application.");
    }
  };

  return (
    <div className="w-full min-h-screen">
      <div className="flex flex-row w-full">
        {/* LEFT PANEL */}
        <div className="bg-gray-100 p-4 px-10 w-2/3 gap-4 flex flex-col text-theme ">
          <h2 className="text-center text-theme font-semibold text-xl">
            {data?.title}
          </h2>

          {/* Basic Detail List */}
          <div className="flex flex-col gap-2">
            {basicDetails.map((item, idx) => (
              <DetailCard key={idx} label={item.label} value={item.value} />
            ))}
          </div>

          {/* Extra Lists */}
          {extraSections.map((sec, idx) => {
            const listItems = Array.isArray(sec.value)
              ? sec.value
              : typeof sec.value === "string"
              ? sec.value.split(/[,|.|-]/) // split on comma OR hyphen
              : [];

            return (
              <div key={idx}>
                <h2 className="text-sm font-semibold">{sec.title}:</h2>

                {listItems.length > 0 ? (
                  <ul className="list-disc pl-5 text-sm">
                    {listItems.map((x, i) => (
                      <li key={i}>{x.trim()}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm">-</p>
                )}
              </div>
            );
          })}
        </div>

        {/* RIGHT PANEL */}
        <div className="w-full p-4 px-10">
          <div className="flex flex-col gap-4">
            <span>
              <h2 className="text-center text-theme font-semibold text-xl">
                Job Application Form
              </h2>
              <p className="text-theme/60 text-sm text-center">
                Fill in your details below to apply.
              </p>
            </span>

            <CommonForm
              fields={formFields}
              onSubmit={handleSubmit}
              showDraftButton={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobApplicationForm;
